import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import RedactionNotice from "../components/RedactionNotice";
import PrivateNotes from "../components/PrivateNotes";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { ArrowLeft, PaperPlane, UploadSimple, File as FileIcon, Image, CheckCircle, XCircle, ChatCircle, DownloadSimple, ArrowsClockwise, ShieldCheck, Camera, X, PencilSimple, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadAuthFile } from "../lib/fileOpen";
import { ImageAnnotator } from "../components/ImageAnnotator";
import ShareTrackerButton from "../components/ShareTrackerButton";
import ReassignmentWarning from "../components/ReassignmentWarning";
import TaskOperativeChips from "../components/TaskOperativeChips";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
const TASK_STATUSES = ["not_started", "assigned", "in_progress", "awaiting_materials", "awaiting_review", "completed_by_vendor", "approved", "rejected", "cancelled"];

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium status-${status}`}>{status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>;
}

function PriorityBadge({ priority }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium priority-${priority}`}>{priority?.toUpperCase()}</span>;
}

export default function TaskDetailPage() {
  const { projectId, taskId } = useParams();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [recurringForm, setRecurringForm] = useState({ recurrence_pattern: "weekly", recurrence_interval: 1, recurrence_end_date: "" });
  // Phase 276 — Edit Task dialog (title/description/dates/times/sessions/budget/due)
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", priority: "medium", category: "general", due_date: "", start_date: "", start_time: "", end_date: "", end_time: "", budget: "", scheduled_dates: [], assigned_to: "", assigned_type: "", assigned_to_name: "" });
  const [rejectReason, setRejectReason] = useState("");
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [inHouseTeam, setInHouseTeam] = useState([]);
  const [taskOperatives, setTaskOperatives] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [subtasks, setSubtasks] = useState([]);
  const fileInputRef = useRef(null);
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const isVendor = user?.role === "vendor";
  const [taskComplianceDocs, setTaskComplianceDocs] = useState([]);
  const [showComplete, setShowComplete] = useState(false);
  const [completionNotes, setCompletionNotes] = useState("");
  const [completionImages, setCompletionImages] = useState([]);
  const [completionPreviews, setCompletionPreviews] = useState([]);
  const [uploadingCompletion, setUploadingCompletion] = useState(false);
  const [annotatingIdx, setAnnotatingIdx] = useState(null);
  const completionFileRef = useRef(null);

  const fetchTask = useCallback(async () => {
    try {
      const { data } = await API.get(`/tasks/${taskId}`);
      setTask(data);
      setTaskOperatives(data.assigned_operatives || []);
      // Fetch subtasks
      const { data: subs } = await API.get(`/tasks/${taskId}/subtasks`);
      setSubtasks(subs);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { fetchTask(); }, [fetchTask]);

  useEffect(() => {
    if (isAdmin) {
      API.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
      API.get("/suppliers").then(({ data }) => setVendors(data)).catch(() => {});
      API.get("/in-house/team").then(({ data }) => setInHouseTeam(data.team || [])).catch(() => {});
    }
    API.get(`/tasks/${taskId}/compliance-docs`).then(({ data }) => setTaskComplianceDocs(data)).catch(() => {});
  }, [isAdmin, taskId]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await API.post(`/tasks/${taskId}/comments`, { text: comment });
      setComment("");
      fetchTask();
      toast.success("Comment added");
    } catch { toast.error("Failed to add comment"); }
  };

  const toggleClientVisible = async (f, currentlyShown) => {
    try {
      const { data } = await API.put(`/tasks/${taskId}/files/${f.id}/client-visibility`, { visible: !currentlyShown });
      const noun = f.kind === "photo" ? "Photo" : "Document";
      toast.success(data.client_visible ? `${noun} now visible in the client portal` : `${noun} hidden from the client portal`);
      fetchTask();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await API.post(`/upload?entity_type=task&entity_id=${taskId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success("Files uploaded");
      fetchTask();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await API.put(`/tasks/${taskId}/status`, { status: newStatus });
      toast.success("Status updated");
      fetchTask();
    } catch { toast.error("Failed to update status"); }
  };

  const handleAddCompletionImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCompletionImages(prev => [...prev, ...files]);
    const newPreviews = files.map(f => ({ name: f.name, url: URL.createObjectURL(f), type: f.type }));
    setCompletionPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeCompletionImage = (idx) => {
    URL.revokeObjectURL(completionPreviews[idx]?.url);
    setCompletionImages(prev => prev.filter((_, i) => i !== idx));
    setCompletionPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAnnotationSave = (blob) => {
    const idx = annotatingIdx;
    if (idx === null) return;
    const oldPreview = completionPreviews[idx];
    const fileName = oldPreview?.name?.replace(/\.[^.]+$/, "") + "_annotated.png";
    const newFile = new File([blob], fileName, { type: "image/png" });
    const newUrl = URL.createObjectURL(blob);
    // Revoke old URL
    if (oldPreview?.url) URL.revokeObjectURL(oldPreview.url);
    // Replace file and preview at index
    setCompletionImages(prev => prev.map((f, i) => i === idx ? newFile : f));
    setCompletionPreviews(prev => prev.map((p, i) => i === idx ? { name: fileName, url: newUrl, type: "image/png" } : p));
    setAnnotatingIdx(null);
    toast.success("Annotation saved");
  };

  const handleCompleteWithImages = async () => {
    setUploadingCompletion(true);
    try {
      // Upload all images first
      const uploadedFileIds = [];
      for (const file of completionImages) {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await API.post(`/upload?entity_type=task&entity_id=${taskId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        if (data?.id) uploadedFileIds.push(data.id);
      }
      // Update status with completion data
      await API.put(`/tasks/${taskId}/status`, {
        status: "completed_by_vendor",
        completion_notes: completionNotes.trim() || undefined,
        completion_images: uploadedFileIds.length > 0 ? uploadedFileIds : undefined,
      });
      toast.success("Task marked as complete");
      setShowComplete(false);
      setCompletionNotes("");
      setCompletionImages([]);
      completionPreviews.forEach(p => URL.revokeObjectURL(p.url));
      setCompletionPreviews([]);
      fetchTask();
    } catch { toast.error("Failed to complete task"); }
    finally { setUploadingCompletion(false); }
  };

  const handleAssign = async () => {
    if (!assignTo) return;
    const assignUser = users.find(u => u._id === assignTo);
    try {
      await API.put(`/tasks/${taskId}/assign`, {
        assigned_to: assignTo,
        assigned_to_name: assignUser?.name || "",
        assigned_type: assignUser?.role === "vendor" ? "vendor" : "team"
      });
      toast.success("Task assigned");
      setShowAssign(false);
      fetchTask();
    } catch { toast.error("Failed to assign"); }
  };

  const handleApprove = async () => {
    try {
      await API.put(`/tasks/${taskId}/approve`, { notes: "Approved" });
      toast.success("Task approved and signed off");
      fetchTask();
    } catch { toast.error("Failed to approve"); }
  };

  const handleReject = async () => {
    try {
      await API.put(`/tasks/${taskId}/reject`, { reason: rejectReason });
      toast.success("Task rejected");
      setShowReject(false);
      setRejectReason("");
      fetchTask();
    } catch { toast.error("Failed to reject"); }
  };

  const handleSetRecurring = async () => {
    try {
      await API.put(`/tasks/${taskId}/set-recurring`, {
        is_recurring: true,
        ...recurringForm,
        recurrence_interval: parseInt(recurringForm.recurrence_interval) || 1
      });
      toast.success("Recurring schedule set");
      setShowRecurring(false);
      fetchTask();
    } catch { toast.error("Failed to set recurring"); }
  };

  const handleRemoveRecurring = async () => {
    try {
      await API.put(`/tasks/${taskId}/set-recurring`, { is_recurring: false });
      toast.success("Recurring schedule removed");
      fetchTask();
    } catch { toast.error("Failed to update"); }
  };

  const openEdit = () => {
    setEditForm({
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "medium",
      category: task.category || "general",
      due_date: task.due_date || "",
      start_date: task.start_date || "",
      start_time: task.start_time || "",
      end_date: task.end_date || "",
      end_time: task.end_time || "",
      budget: task.budget || task.estimated_cost || "",
      scheduled_dates: Array.isArray(task.scheduled_dates) ? task.scheduled_dates.map(s => ({ ...s })) : [],
      assigned_to: task.assigned_to || "",
      assigned_type: task.assigned_type || "",
      assigned_to_name: task.assigned_to_name || "",
    });
    setShowEdit(true);
  };

  const handleEditSave = async () => {
    if (!editForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      const payload = { ...editForm };
      // Strip assignee fields from the PUT body — they're handled by a separate /assign call
      delete payload.assigned_to;
      delete payload.assigned_type;
      delete payload.assigned_to_name;
      // Sanitise scheduled_dates — drop blanks
      payload.scheduled_dates = (payload.scheduled_dates || []).filter(s => s.start_date);
      if (payload.budget === "") delete payload.budget;
      else payload.budget = parseFloat(payload.budget) || 0;
      await API.put(`/tasks/${taskId}`, payload);

      // Reassign if the assignee changed (covers add / change / clear)
      const newAssignee = editForm.assigned_to || "";
      const currentAssignee = task.assigned_to || "";
      if (newAssignee !== currentAssignee || editForm.assigned_type !== (task.assigned_type || "")) {
        const assignPayload = {
          assigned_to: newAssignee,
          assigned_to_name: editForm.assigned_to_name || "",
          assigned_type: editForm.assigned_type || "",
        };
        if (editForm.assigned_type === "vendor" && newAssignee) {
          const v = vendors.find(x => x.id === newAssignee || x._id === newAssignee);
          if (v) {
            assignPayload.vendor_id = v.id || v._id;
            assignPayload.vendor_name = v.company_name || v.name || editForm.assigned_to_name;
            assignPayload.assigned_to_name = v.company_name || v.name || editForm.assigned_to_name;
          }
        }
        await API.put(`/tasks/${taskId}/assign`, assignPayload);
      }

      toast.success("Task updated");
      setShowEdit(false);
      fetchTask();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update task");
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const response = await API.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!task) return <div className="text-center py-12 text-muted-foreground">Task not found</div>;

  const isLocked = task.status === "approved" && !isAdmin;

  return (
    <div data-testid="task-detail-page">
      <Link to={`/projects/${projectId}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Back to Project
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <h1 className="text-2xl font-bold font-[Chivo] tracking-tight" data-testid="task-title">{task.title}</h1>
                <p className="text-sm text-muted-foreground">{task.reference} | {task.project_name}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <StatusBadge status={task.status} />
                <PriorityBadge priority={task.priority} />
                {isVendor && <RedactionNotice compact />}
              </div>
            </div>
            {task.description && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{task.description}</p>}
          </div>

          {/* Task Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-border rounded-md p-3"><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Category</p><p className="text-sm font-medium capitalize">{task.category?.replace(/_/g," ")}</p></div>
            <div className="border border-border rounded-md p-3"><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Due Date</p><p className="text-sm font-medium">{fmtDate(task.due_date) || "-"}</p></div>
            <div className="border border-border rounded-md p-3"><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Budget</p><p className="text-sm font-medium">£{(task.budget || task.estimated_cost || 0).toLocaleString()}</p></div>
            <div className="border border-border rounded-md p-3"><p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Assigned To</p><p className="text-sm font-medium">{task.assigned_to_name || "Unassigned"}</p></div>
          </div>

          {/* Phase 276 — Schedule block */}
          {(task.start_date || (task.scheduled_dates || []).filter(s => s.start_date).length > 0) && (
            <div className="border border-border rounded-md p-3 bg-muted/30" data-testid="task-schedule-block">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-semibold">Schedule</p>
              {task.start_date && (
                <p className="text-sm" data-testid="task-primary-schedule">
                  <span className="font-medium">Primary:</span> {fmtDate(task.start_date)}{task.start_time ? ` · ${task.start_time}` : ""}
                  {task.end_date && task.end_date !== task.start_date && ` → ${task.end_date}`}
                  {task.end_time && task.end_time !== task.start_time ? ` · ${task.end_time}` : ""}
                </p>
              )}
              {Array.isArray(task.scheduled_dates) && task.scheduled_dates.filter(s => s.start_date).map((s, idx) => (
                <p key={idx} className="text-sm mt-1" data-testid={`task-session-display-${idx}`}>
                  <span className="font-medium">Session {idx + 2}:</span> {fmtDate(s.start_date)}{s.start_time ? ` · ${s.start_time}` : ""}
                  {s.end_date && s.end_date !== s.start_date && ` → ${s.end_date}`}
                  {s.end_time && s.end_time !== s.start_time ? ` · ${s.end_time}` : ""}
                </p>
              ))}
            </div>
          )}

          {/* Files / Evidence */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold font-[Chivo]">Files & Evidence</h3>
              {!isLocked && (
                <>
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
                    <UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading..." : "Upload"}
                  </Button>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" />
                </>
              )}
            </div>
            {(task.files || []).length === 0 ? (
              <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-6 text-center">No files uploaded yet</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {task.files.map(f => (
                  <div key={f.id} className="flex items-center gap-3 border border-border rounded-md p-3 hover:bg-muted/30 transition-colors" data-testid={`file-${f.id}`}>
                    {f.content_type?.startsWith("image/") ? <Image size={20} className="text-blue-500 shrink-0" /> : <FileIcon size={20} className="text-muted-foreground shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{f.original_filename}</p>
                      <p className="text-[10px] text-muted-foreground">{f.created_at ? new Date(f.created_at).toLocaleString() : ""}{f.origin === "portal" ? ` · ${f.kind === "photo" ? "photo" : "document"} via ${f.category ? `${f.category} · ` : ""}portal upload` : ""}{f.kind === "photo" ? (f.client_visible === false ? " · hidden from client" : "") : (f.client_visible ? " · shared with client" : "")}</p>
                    </div>
                    {isAdmin && (() => { const shown = f.kind === "photo" ? f.client_visible !== false : f.client_visible === true; return (
                      <Button variant="ghost" size="icon" className={`shrink-0 h-7 w-7 ${shown ? "text-emerald-600" : "text-muted-foreground"}`}
                        title={shown ? "Visible in the client portal — click to hide" : `Hidden from the client portal — click to ${f.kind === "photo" ? "show" : "share"}`}
                        onClick={() => toggleClientVisible(f, shown)} data-testid={`client-visible-${f.id}`}>
                        {shown ? <Eye size={14} weight="fill" /> : <EyeSlash size={14} />}
                      </Button>); })()}
                    <Button variant="ghost" size="icon" className="shrink-0 h-7 w-7" onClick={() => f.origin === "portal" && f.file_url ? downloadAuthFile(f.file_url, f.original_filename) : downloadFile(f.id, f.original_filename)} data-testid={`download-${f.id}`}>
                      <DownloadSimple size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Completion Evidence */}
          {(task.completion_notes || task.completion_images?.length > 0) && (
            <div data-testid="completion-evidence-section">
              <h3 className="text-sm font-semibold font-[Chivo] mb-3 flex items-center gap-2">
                <CheckCircle size={16} className="text-emerald-500" /> Completion Evidence
              </h3>
              {task.completion_notes && (
                <div className="border border-border rounded-md p-3 mb-3 bg-emerald-50 dark:bg-emerald-900/10">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm" data-testid="completion-notes-display">{task.completion_notes}</p>
                </div>
              )}
              {task.completion_images?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="completion-images-display">
                  {task.completion_images.map(imgId => {
                    const imgFile = (task.files || []).find(f => f.id === imgId);
                    return imgFile ? (
                      <div key={imgId} className="border border-border rounded-md overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all" onClick={() => downloadFile(imgFile.id, imgFile.original_filename)}>
                        <div className="h-24 bg-muted flex items-center justify-center">
                          <Image size={24} className="text-muted-foreground" />
                        </div>
                        <p className="text-[9px] text-muted-foreground truncate px-2 py-1">{imgFile.original_filename}</p>
                      </div>
                    ) : (
                      <div key={imgId} className="border border-border rounded-md p-3 text-center text-xs text-muted-foreground">
                        <Image size={20} className="mx-auto mb-1" />
                        Image
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Dependencies */}
          {task.dependencies?.length > 0 && (
            <div data-testid="task-dependencies">
              <h3 className="text-sm font-semibold font-[Chivo] mb-2">Dependencies</h3>
              <div className="space-y-1">
                {task.dependencies.map(depId => (
                  <Link key={depId} to={`/projects/${projectId}/tasks/${depId}`}
                    className="flex items-center gap-2 text-xs p-2 border border-border rounded-md hover:bg-muted/30 transition-colors">
                    <span className="text-muted-foreground">Depends on:</span>
                    <span className="font-medium">{depId.substring(0, 8)}...</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Sub-tasks */}
          {subtasks.length > 0 && (
            <div data-testid="subtasks-section">
              <h3 className="text-sm font-semibold font-[Chivo] mb-2">Sub-tasks ({subtasks.length})</h3>
              <div className="border border-border rounded-md divide-y divide-border">
                {subtasks.map(st => (
                  <Link key={st.id} to={`/projects/${projectId}/tasks/${st.id}`}
                    className="flex items-center justify-between px-3 py-2 hover:bg-muted/30 transition-colors" data-testid={`subtask-${st.id}`}>
                    <div>
                      <p className="text-sm font-medium">{st.title}</p>
                      <p className="text-[10px] text-muted-foreground">{st.reference}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={st.priority} />
                      <StatusBadge status={st.status} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Compliance Documents */}
          {taskComplianceDocs.length > 0 && (
            <div data-testid="task-compliance-section">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={16} className="text-primary" />
                <h3 className="text-sm font-semibold font-[Chivo]">Compliance Documents ({taskComplianceDocs.length})</h3>
              </div>
              <div className="border border-border rounded-md divide-y divide-border">
                {taskComplianceDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 transition-colors" data-testid={`task-compliance-${doc.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{doc.title || doc.doc_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {doc.doc_type?.replace(/_/g, " ")} | {doc.vendor_name || "N/A"} | {fmtDate(doc.created_at)}
                        {doc.expiry_date && <span> | Expires: {fmtDate(doc.expiry_date)}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {doc.expiry_status === "expired" && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">EXPIRED</span>
                      )}
                      {doc.expiry_status === "expiring_soon" && (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 animate-pulse">EXPIRING</span>
                      )}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                        doc.status === "verified" ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" :
                        doc.status === "rejected" ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400" :
                        "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                      }`}>{doc.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                      {doc.file_name && (
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => {
                          try {
                            const resp = await API.get(`/task-compliance/${doc.id}/download`, { responseType: "blob" });
                            const url = URL.createObjectURL(resp.data);
                            const a = document.createElement("a"); a.href = url; a.download = doc.file_name; a.click(); URL.revokeObjectURL(url);
                          } catch { toast.error("Download failed"); }
                        }} title="Download" data-testid={`download-task-compliance-${doc.id}`}><DownloadSimple size={14} /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Comments */}
          <div>
            <h3 className="text-sm font-semibold font-[Chivo] mb-3 flex items-center gap-2"><ChatCircle size={16} /> Comments</h3>
            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
              {(task.comments || []).map(c => (
                <div key={c.id} className="border border-border rounded-md p-3" data-testid={`comment-${c.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium">{c.user_name}</span>
                    <Badge variant="outline" className="text-[9px] py-0">{c.user_role?.replace(/_/g," ")}</Badge>
                    <span className="text-[10px] text-muted-foreground ml-auto">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm">{c.text}</p>
                </div>
              ))}
              {(!task.comments || task.comments.length === 0) && <p className="text-sm text-muted-foreground">No comments yet</p>}
            </div>
            {!isLocked && (
              <form onSubmit={handleComment} className="flex gap-2">
                <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment..." className="flex-1" data-testid="comment-input" />
                <Button type="submit" size="sm" data-testid="submit-comment-btn"><PaperPlane size={14} /></Button>
              </form>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Task Team (in-house operatives) */}
          {(isAdmin || taskOperatives.length > 0) && (
            <div className="border border-border rounded-md p-4" data-testid="task-team-panel">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Task Team</h3>
              <TaskOperativeChips
                taskId={taskId}
                operatives={taskOperatives}
                team={inHouseTeam}
                canEdit={isAdmin && !isLocked}
                onChange={(list) => { setTaskOperatives(list); fetchTask(); }}
                testIdPrefix="task-team"
              />
            </div>
          )}

          {/* Actions */}
          <div className="border border-border rounded-md p-4 space-y-3">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Actions</h3>

            {/* Supplier actions */}
            {isVendor && !isLocked && (
              <>
                {["assigned", "in_progress", "awaiting_materials"].includes(task.status) && (
                  <Button className="w-full" size="sm" onClick={() => handleStatusChange("in_progress")} data-testid="start-task-btn">Start Working</Button>
                )}
                {task.status === "in_progress" && (
                  <Button className="w-full" size="sm" onClick={() => setShowComplete(true)} data-testid="complete-task-btn">Mark Complete</Button>
                )}
                {task.status === "rejected" && (
                  <Button className="w-full" size="sm" onClick={() => handleStatusChange("in_progress")} data-testid="rework-task-btn">Start Rework</Button>
                )}
              </>
            )}

            {/* Admin actions */}
            {isAdmin && (
              <>
                <Button variant="default" className="w-full" size="sm" onClick={openEdit} data-testid="edit-task-btn">
                  <PencilSimple size={14} className="mr-1" /> Edit Task
                </Button>
                <Button variant="outline" className="w-full" size="sm" onClick={() => setShowAssign(true)} data-testid="assign-task-btn">Assign Task</Button>
                <ShareTrackerButton
                  taskId={taskId}
                  defaultEmail={task.client_email || ""}
                  data-testid="share-tracker-btn"
                />
                <Button variant="outline" className="w-full" size="sm" onClick={() => setShowRecurring(true)} data-testid="set-recurring-btn">
                  <ArrowsClockwise size={14} className="mr-1" /> {task.is_recurring ? "Edit Recurring" : "Set Recurring"}
                </Button>
                {task.status === "completed_by_vendor" && (
                  <>
                    <Button className="w-full" size="sm" onClick={handleApprove} data-testid="approve-task-btn">
                      <CheckCircle size={14} className="mr-1" /> Approve & Sign Off
                    </Button>
                    <Button variant="destructive" className="w-full" size="sm" onClick={() => setShowReject(true)} data-testid="reject-task-btn">
                      <XCircle size={14} className="mr-1" /> Reject
                    </Button>
                  </>
                )}
                <Select value={task.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-full" data-testid="task-status-dropdown"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
          </div>

          {/* Timeline */}
          <div className="border border-border rounded-md p-4">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Timeline</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{task.created_at ? new Date(task.created_at).toLocaleDateString() : "-"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Assigned</span><span>{task.assigned_at ? new Date(task.assigned_at).toLocaleDateString() : "-"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Started</span><span>{task.started_at ? new Date(task.started_at).toLocaleDateString() : "-"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Completed</span><span>{task.completed_at ? new Date(task.completed_at).toLocaleDateString() : "-"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Signed Off</span><span>{task.signed_off_at ? new Date(task.signed_off_at).toLocaleDateString() : "-"}</span></div>
            </div>
          </div>

          {/* Recurring Info */}
          {task.is_recurring && (
            <div className="border border-border rounded-md p-4" data-testid="recurring-info">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Recurring Schedule</h3>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Pattern</span><span className="capitalize font-medium">{task.recurrence_pattern || "weekly"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Interval</span><span>Every {task.recurrence_interval || 1} {task.recurrence_pattern || "week"}(s)</span></div>
                {task.recurrence_end_date && <div className="flex justify-between"><span className="text-muted-foreground">Until</span><span>{task.recurrence_end_date}</span></div>}
              </div>
              {isAdmin && (
                <Button variant="ghost" size="sm" className="w-full mt-2 text-destructive hover:text-destructive text-xs" onClick={handleRemoveRecurring} data-testid="remove-recurring-btn">
                  Remove Recurring
                </Button>
              )}
            </div>
          )}

          {/* Approval */}
          {task.approval_status !== "pending" && (
            <div className="border border-border rounded-md p-4">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Approval</h3>
              <StatusBadge status={task.approval_status} />
              {task.approved_by_name && <p className="text-xs text-muted-foreground mt-2">By: {task.approved_by_name}</p>}
              {task.approved_at && <p className="text-xs text-muted-foreground">At: {new Date(task.approved_at).toLocaleString()}</p>}
            </div>
          )}

          {/* Private Notes — admins only */}
          {!isVendor && <PrivateNotes entityType="task" entityId={task.id} />}
        </div>
      </div>

      {/* Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent data-testid="assign-task-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Assign Task</DialogTitle><DialogDescription>Select a supplier or team member to assign this task to.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Assign To</Label>
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger data-testid="assign-user-select"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                <SelectContent>
                  {vendors.filter(v => v.status === "active" && v.user_id).length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Suppliers</div>
                      {vendors.filter(v => v.status === "active" && v.user_id).map(v => (
                        <SelectItem key={v.user_id} value={v.user_id}>{v.contact_name || v.company_name} ({v.trade || "Supplier"})</SelectItem>
                      ))}
                    </>
                  )}
                  {users.filter(u => u.active && u.role !== "vendor").length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mt-1">Team Members</div>
                      {users.filter(u => u.active && u.role !== "vendor").map(u => (
                        <SelectItem key={u._id} value={u._id}>{u.name} ({u.role?.replace(/_/g," ")})</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            {assignTo && (
              <ReassignmentWarning
                candidateId={assignTo}
                entityType="task"
                entityId={taskId}
                projectId={task.project_id}
                trade={task.category || ""}
                onAcceptSuggestion={(s) => setAssignTo(s.supplier_user_id || s.supplier_id)}
                data-testid="task-reassign-warning"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} data-testid="confirm-assign-btn">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent data-testid="reject-task-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Reject Task</DialogTitle></DialogHeader>
          <div>
            <Label>Reason for Rejection</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} placeholder="Describe what needs to be reworked..." data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">Reject Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Dialog */}
      <Dialog open={showRecurring} onOpenChange={setShowRecurring}>
        <DialogContent data-testid="set-recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Set Recurring Schedule</DialogTitle>
            <DialogDescription>Configure automatic task recurrence.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Pattern</Label>
              <Select value={recurringForm.recurrence_pattern} onValueChange={v => setRecurringForm({...recurringForm, recurrence_pattern: v})}>
                <SelectTrigger data-testid="recurring-pattern-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Interval (every X {recurringForm.recurrence_pattern === "daily" ? "days" : recurringForm.recurrence_pattern === "weekly" ? "weeks" : "months"})</Label>
              <Input type="number" min={1} max={52} value={recurringForm.recurrence_interval} onChange={e => setRecurringForm({...recurringForm, recurrence_interval: e.target.value})} data-testid="recurring-interval-input" />
            </div>
            <div>
              <Label>End Date (optional)</Label>
              <DateInput value={recurringForm.recurrence_end_date} onChange={e => setRecurringForm({...recurringForm, recurrence_end_date: e.target.value})} data-testid="recurring-end-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecurring(false)}>Cancel</Button>
            <Button onClick={handleSetRecurring} data-testid="confirm-recurring-btn">Set Recurring</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Dialog with Multi-Image Upload */}
      <Dialog open={showComplete} onOpenChange={(open) => {
        if (!open) {
          completionPreviews.forEach(p => URL.revokeObjectURL(p.url));
          setCompletionImages([]);
          setCompletionPreviews([]);
          setCompletionNotes("");
        }
        setShowComplete(open);
      }}>
        <DialogContent className="max-w-lg" data-testid="complete-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Mark Task Complete</DialogTitle>
            <DialogDescription>Upload evidence images and add completion notes before submitting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Completion Images</Label>
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                onClick={() => completionFileRef.current?.click()}
                data-testid="completion-images-dropzone"
              >
                <Camera size={28} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click to upload images</p>
                <p className="text-[10px] text-muted-foreground mt-1">Supports multiple images (JPG, PNG, HEIC)</p>
              </div>
              <input
                ref={completionFileRef}
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleAddCompletionImages}
                data-testid="completion-images-input"
              />
              {completionPreviews.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3" data-testid="completion-images-preview">
                  {completionPreviews.map((p, i) => (
                    <div key={i} className="relative group rounded-md overflow-hidden border border-border">
                      <img src={p.url} alt={p.name} className="w-full h-20 object-cover" />
                      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => setAnnotatingIdx(i)}
                          className="bg-blue-600/80 text-white rounded-full p-0.5"
                          title="Annotate"
                          data-testid={`annotate-completion-image-${i}`}
                        >
                          <PencilSimple size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeCompletionImage(i)}
                          className="bg-black/60 text-white rounded-full p-0.5"
                          data-testid={`remove-completion-image-${i}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <p className="text-[8px] text-muted-foreground truncate px-1 py-0.5">{p.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Completion Notes (optional)</Label>
              <Textarea
                value={completionNotes}
                onChange={e => setCompletionNotes(e.target.value)}
                rows={3}
                placeholder="Describe the work completed, any issues encountered..."
                data-testid="completion-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplete(false)} disabled={uploadingCompletion}>Cancel</Button>
            <Button onClick={handleCompleteWithImages} disabled={uploadingCompletion} data-testid="confirm-complete-btn">
              {uploadingCompletion ? (
                <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Uploading...</span>
              ) : (
                <span className="flex items-center gap-1"><CheckCircle size={14} /> Mark Complete</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Annotation Dialog */}
      <Dialog open={annotatingIdx !== null} onOpenChange={(open) => { if (!open) setAnnotatingIdx(null); }}>
        <DialogContent className="max-w-[960px] max-h-[90vh] overflow-y-auto" data-testid="annotation-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Annotate Image</DialogTitle>
            <DialogDescription>Draw circles, arrows, or freehand marks to highlight specific areas.</DialogDescription>
          </DialogHeader>
          {annotatingIdx !== null && completionPreviews[annotatingIdx] && (
            <ImageAnnotator
              imageSrc={completionPreviews[annotatingIdx].url}
              onSave={handleAnnotationSave}
              onCancel={() => setAnnotatingIdx(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 276 — Edit Task Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Edit Task</DialogTitle>
            <DialogDescription>Update the task's title, schedule, assignment and budget.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="mt-1" data-testid="edit-task-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={3} className="mt-1" data-testid="edit-task-description" />
            </div>

            {/* Phase 278 — Unified Assignee picker (in-house OR supplier) */}
            <div className="border border-dashed border-border rounded-md p-3 bg-muted/20" data-testid="edit-task-assignee-block">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Assigned to</Label>
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2 mt-2 items-end">
                <div>
                  <Label className="text-[10px]">Type</Label>
                  <Select
                    value={editForm.assigned_type || "none"}
                    onValueChange={v => {
                      if (v === "none") {
                        setEditForm({ ...editForm, assigned_type: "", assigned_to: "", assigned_to_name: "" });
                      } else {
                        // Type changed → clear the previous selection so the picker re-fills correctly
                        setEditForm({ ...editForm, assigned_type: v, assigned_to: "", assigned_to_name: "" });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="edit-task-assignee-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      <SelectItem value="in_house">In-house operative</SelectItem>
                      <SelectItem value="vendor">Supplier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Person / Company</Label>
                  {editForm.assigned_type === "in_house" ? (
                    <Select
                      value={editForm.assigned_to || ""}
                      onValueChange={v => {
                        const u = (inHouseTeam.length > 0 ? inHouseTeam : users).find(x => (x._id || x.id) === v);
                        const name = u ? (u.name || u.email || "") : "";
                        setEditForm({ ...editForm, assigned_to: v, assigned_to_name: name });
                      }}
                    >
                      <SelectTrigger data-testid="edit-task-assignee-inhouse"><SelectValue placeholder="Pick an operative" /></SelectTrigger>
                      <SelectContent>
                        {(inHouseTeam.length > 0 ? inHouseTeam : users.filter(u => u.role === "in_house")).map(u => (
                          <SelectItem key={u._id || u.id} value={u._id || u.id}>{u.name || u.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : editForm.assigned_type === "vendor" ? (
                    <Select
                      value={editForm.assigned_to || ""}
                      onValueChange={v => {
                        const ve = vendors.find(x => (x.id || x._id) === v);
                        const name = ve ? (ve.company_name || ve.name || "") : "";
                        setEditForm({ ...editForm, assigned_to: v, assigned_to_name: name });
                      }}
                    >
                      <SelectTrigger data-testid="edit-task-assignee-vendor"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (
                          <SelectItem key={v.id || v._id} value={v.id || v._id}>{v.company_name || v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value="" placeholder="Set type first" disabled className="text-muted-foreground" />
                  )}
                </div>
                {editForm.assigned_to && (
                  <Button type="button" variant="ghost" size="sm" className="h-9 text-[10px] text-muted-foreground hover:text-red-500" onClick={() => setEditForm({ ...editForm, assigned_type: "", assigned_to: "", assigned_to_name: "" })} data-testid="edit-task-clear-assignee">
                    Clear
                  </Button>
                )}
              </div>
              {editForm.assigned_to_name && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  ➜ Saving will assign to <strong>{editForm.assigned_to_name}</strong>{editForm.assigned_type === "in_house" ? " (in-house operative)" : " (supplier)"} and email them the schedule (with .ics).
                </p>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={editForm.category} onValueChange={v => setEditForm({ ...editForm, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-task-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="painting">Painting</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="grounds">Grounds</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={editForm.priority} onValueChange={v => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-task-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Budget (£)</Label>
                <Input type="number" min="0" step="0.01" value={editForm.budget} onChange={e => setEditForm({ ...editForm, budget: e.target.value })} className="mt-1" data-testid="edit-task-budget" />
              </div>
            </div>
            <div>
              <Label>Due Date <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
              <DateInput value={editForm.due_date} onChange={e => setEditForm({ ...editForm, due_date: e.target.value })} className="mt-1 max-w-[200px]" data-testid="edit-task-due-date" />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label>Start Date</Label>
                <DateInput value={editForm.start_date} onChange={e => setEditForm({ ...editForm, start_date: e.target.value })} className="mt-1" data-testid="edit-task-start-date" />
              </div>
              <div>
                <Label>End Date <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
                <DateInput value={editForm.end_date} min={editForm.start_date || undefined} onChange={e => setEditForm({ ...editForm, end_date: e.target.value })} className="mt-1" data-testid="edit-task-end-date" />
              </div>
              <div>
                <Label>Start Time</Label>
                <Input type="time" value={editForm.start_time} onChange={e => setEditForm({ ...editForm, start_time: e.target.value })} className="mt-1" data-testid="edit-task-start-time" />
              </div>
              <div>
                <Label>End Time</Label>
                <Input type="time" value={editForm.end_time} onChange={e => setEditForm({ ...editForm, end_time: e.target.value })} className="mt-1" data-testid="edit-task-end-time" />
              </div>
            </div>

            {/* Additional sessions repeater */}
            <div className="border-l-2 border-l-primary/30 pl-3 ml-1 space-y-2" data-testid="edit-task-sessions">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Additional sessions <span className="opacity-60">(non-consecutive days)</span></Label>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setEditForm({ ...editForm, scheduled_dates: [...(editForm.scheduled_dates || []), { start_date: "", start_time: "", end_date: "", end_time: "" }] })} data-testid="edit-task-add-session-btn">
                  + Add session
                </Button>
              </div>
              {(editForm.scheduled_dates || []).map((s, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`edit-task-session-${idx}`}>
                  <div>
                    <Label className="text-[10px]">Session {idx + 2} start date</Label>
                    <DateInput value={s.start_date} onChange={e => { const arr = [...editForm.scheduled_dates]; arr[idx] = { ...arr[idx], start_date: e.target.value }; setEditForm({ ...editForm, scheduled_dates: arr }); }} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Start time</Label>
                    <Input type="time" value={s.start_time || ""} onChange={e => { const arr = [...editForm.scheduled_dates]; arr[idx] = { ...arr[idx], start_time: e.target.value }; setEditForm({ ...editForm, scheduled_dates: arr }); }} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px]">End date</Label>
                    <DateInput value={s.end_date || ""} min={s.start_date || undefined} onChange={e => { const arr = [...editForm.scheduled_dates]; arr[idx] = { ...arr[idx], end_date: e.target.value }; setEditForm({ ...editForm, scheduled_dates: arr }); }} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px]">End time</Label>
                    <Input type="time" value={s.end_time || ""} onChange={e => { const arr = [...editForm.scheduled_dates]; arr[idx] = { ...arr[idx], end_time: e.target.value }; setEditForm({ ...editForm, scheduled_dates: arr }); }} className="mt-1" />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-500 hover:text-red-700" onClick={() => setEditForm({ ...editForm, scheduled_dates: editForm.scheduled_dates.filter((_, i) => i !== idx) })} data-testid={`edit-task-session-${idx}-remove`}>
                    <X size={14} />
                  </Button>
                </div>
              ))}
              <p className="text-[10px] text-muted-foreground/80">Use this to split a task across non-consecutive days (e.g. Mon AND Thu).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleEditSave} data-testid="confirm-edit-task-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
